import React, { useState } from 'react';

const StudioList = ({ studios = [], onUpdateStatus, onEditStudio }) => {
    const [selectedStudio, setSelectedStudio] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    if (!studios) return <div className="text-white">No studio data available</div>;

    const handleEditClick = () => {
        setEditForm({ ...selectedStudio });
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditForm({});
    };

    const handleSave = async () => {
        if (!onEditStudio) return;
        setIsSaving(true);

        // Ensure settings-related fields are properly nested for the backend
        const payload = { ...editForm };
        payload.settings = {
            ...(payload.settings || {}),
            city: payload.city !== undefined ? payload.city : selectedStudio.city,
            region: payload.region !== undefined ? payload.region : selectedStudio.region,
            postcode: payload.postcode !== undefined ? payload.postcode : selectedStudio.postcode,
            website: payload.website !== undefined ? payload.website : selectedStudio.website,
            email_company_name: payload.email_company_name !== undefined ? payload.email_company_name : selectedStudio.email_company_name,
            currency: payload.currency !== undefined ? payload.currency : selectedStudio.currency,
            timezone: payload.timezone !== undefined ? payload.timezone : selectedStudio.timezone
        };

        // Call the parent's onEditStudio function (returns bool)
        const success = await onEditStudio(selectedStudio.id, payload);
        setIsSaving(false);
        if (success) {
            setIsEditing(false);
            // Optionally, update selectedStudio locally or wait for parent to re-render
            setSelectedStudio({ ...selectedStudio, ...editForm, settings: payload.settings });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const getStatusStyles = (status) => {
        const s = (status || 'Active').toLowerCase();
        if (s === 'approved' || s === 'active') return 'bg-green-500/20 text-green-400';
        if (s === 'suspended') return 'bg-orange-500/20 text-orange-400';
        if (s === 'removed') return 'bg-red-500/20 text-red-400';
        return 'bg-yellow-500/20 text-yellow-400';
    };

    return (
        <div className="space-y-6 animate-fade-in-up">
            {/* Filters */}
            <div className="bg-gray-800 p-4 rounded-t-lg border border-gray-700 border-b-0 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span>Show</span>
                    <select className="bg-gray-700 border border-gray-600 rounded px-2 py-1 outline-none text-white font-bold">
                        <option>25</option>
                        <option>50</option>
                        <option>100</option>
                    </select>
                </div>
                <div className="w-full sm:w-auto">
                    <input
                        type="text"
                        placeholder="Search Studios..."
                        className="bg-gray-900 border border-gray-600 rounded-lg px-4 py-2 text-sm w-full sm:w-64 outline-none focus:border-purple-500 placeholder-gray-500 text-white transition"
                    />
                </div>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-b-lg shadow-sm overflow-x-auto custom-scrollbar no-scrollbar mt-0">
                <table className="w-full text-left text-sm text-gray-300">
                    <thead className="bg-gray-900 text-xs uppercase font-bold text-gray-500 border-b border-gray-700">
                        <tr>
                            <th className="px-6 py-4">Studio Name</th>
                            <th className="px-6 py-4">Owner</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Contact</th>
                            <th className="px-6 py-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                        {Array.isArray(studios) && studios.map(s => {
                            const statusLabel = s.status || 'Active';
                            return (
                                <tr key={s.id} className="hover:bg-gray-700/50 group">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            {s.logo ? (
                                                <img src={s.logo} alt={s.studio_name} className="w-10 h-10 rounded-lg object-contain bg-white/5 border border-gray-600" />
                                            ) : (
                                                <div className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-xs bg-gradient-to-br from-purple-500 to-pink-500 text-white`}>
                                                    {s.studio_name ? s.studio_name.substring(0, 2).toUpperCase() : 'ST'}
                                                </div>
                                            )}
                                            <div>
                                                <div className="font-bold text-white text-sm">{s.studio_name}</div>
                                                <div className="text-xs text-gray-500">ID: #{s.id}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-gray-400 font-medium">{s.owner_name || '-'}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusStyles(s.status)}`}>
                                            {statusLabel}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-gray-500">
                                        <div className="text-xs">{s.studio_email}</div>
                                        <div className="text-[10px] opacity-50">{s.studio_phone || '-'}</div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex gap-2 justify-end">
                                            <button onClick={() => setSelectedStudio(s)} className="bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white px-3 py-1.5 rounded text-[10px] font-bold uppercase transition-all">Info</button>
                                            {s.role !== 'superadmin' && s.role !== 'super-admin' && (statusLabel === 'Approved' || statusLabel === 'Active') && (
                                                <button onClick={() => onUpdateStatus && onUpdateStatus(s.id, 'Suspended')} className="bg-orange-600/20 text-orange-400 hover:bg-orange-600 hover:text-white px-3 py-1.5 rounded text-[10px] font-bold uppercase transition-all">Suspend</button>
                                            )}
                                            {s.role !== 'superadmin' && s.role !== 'super-admin' && statusLabel === 'Suspended' && (
                                                <button onClick={() => onUpdateStatus && onUpdateStatus(s.id, 'Approved')} className="bg-green-600/20 text-green-400 hover:bg-green-600 hover:text-white px-3 py-1.5 rounded text-[10px] font-bold uppercase transition-all">Activate</button>
                                            )}
                                            {s.role !== 'superadmin' && s.role !== 'super-admin' && (
                                                <button onClick={() => onUpdateStatus && onUpdateStatus(s.id, 'Removed')} className="bg-red-900/20 text-red-400 hover:bg-red-600 hover:text-white px-3 py-1.5 rounded text-[10px] font-bold uppercase transition-all">Remove</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {studios.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-gray-500">No studios found.</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* STUDIO INFO MODAL */}
            {selectedStudio && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4">
                    <div className="bg-gray-800 rounded-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto border border-gray-700 shadow-2xl relative animate-fade-in-up custom-scrollbar">
                        {/* Header */}
                        <div className="sticky top-0 bg-gray-900/90 backdrop-blur-md px-6 py-4 border-b border-gray-700 flex justify-between items-center z-10">
                            <div className="flex items-center gap-4">
                                {selectedStudio.logo ? (
                                    <img src={selectedStudio.logo} alt={selectedStudio.studio_name} className="w-12 h-12 rounded-lg object-contain bg-white/5 border border-gray-600" />
                                ) : (
                                    <div className="w-12 h-12 rounded-lg flex items-center justify-center text-xl font-bold bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg">
                                        {selectedStudio.studio_name ? selectedStudio.studio_name.substring(0, 2).toUpperCase() : 'ST'}
                                    </div>
                                )}
                                <div>
                                    {isEditing ? (
                                        <input type="text" name="studio_name" value={editForm.studio_name || ''} onChange={handleChange} className="bg-gray-800 border border-purple-500 rounded px-2 py-1 text-xl font-bold text-white mb-1 outline-none w-full" />
                                    ) : (
                                        <h2 className="text-xl font-bold text-white">{selectedStudio.studio_name}</h2>
                                    )}
                                    <p className="text-gray-400 text-sm">Studio ID: #{selectedStudio.id}</p>
                                </div>
                            </div>
                            <button onClick={() => { setSelectedStudio(null); setIsEditing(false); }} className="text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center transition-colors">
                                ✕
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Basic Info */}
                                <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700/50">
                                    <h3 className="text-sm uppercase tracking-wider text-purple-400 font-bold mb-4 flex items-center gap-2">
                                        <span className="text-lg">📋</span> Basic Info
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between border-b border-gray-800 pb-2">
                                            <span className="text-gray-500">Status</span>
                                            <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${getStatusStyles(selectedStudio.status)}`}>
                                                {selectedStudio.status || 'Active'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between border-b border-gray-800 pb-2">
                                            <span className="text-gray-500">Created At</span>
                                            <span className="text-white">{selectedStudio.created_at ? new Date(selectedStudio.created_at).toLocaleDateString() : 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-gray-800 pb-2">
                                            <span className="text-gray-500">License Number</span>
                                            {isEditing ? (
                                                <input type="text" name="license_number" value={editForm.license_number || ''} onChange={handleChange} className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white w-1/2 text-right" />
                                            ) : (
                                                <span className="text-white">{selectedStudio.license_number || 'Not provided'}</span>
                                            )}
                                        </div>
                                        <div className="flex justify-between border-b border-gray-800 pb-2">
                                            <span className="text-gray-500">Staff Count</span>
                                            <span className="text-white">{selectedStudio.staff_count !== undefined ? selectedStudio.staff_count : 'Unknown'}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Details */}
                                <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700/50">
                                    <h3 className="text-sm uppercase tracking-wider text-green-400 font-bold mb-4 flex items-center gap-2">
                                        <span className="text-lg">📞</span> Contact Details
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between border-b border-gray-800 pb-2 items-center">
                                            <span className="text-gray-500">Owner Name</span>
                                            {isEditing ? (
                                                <input type="text" name="owner_name" value={editForm.owner_name || ''} onChange={handleChange} className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white w-1/2 text-right" />
                                            ) : (
                                                <span className="text-white font-medium">{selectedStudio.owner_name}</span>
                                            )}
                                        </div>
                                        <div className="flex justify-between border-b border-gray-800 pb-2 items-center">
                                            <span className="text-gray-500">Studio Email</span>
                                            {isEditing ? (
                                                <input type="email" name="studio_email" value={editForm.studio_email || ''} onChange={handleChange} className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white w-2/3 text-right" />
                                            ) : (
                                                <a href={`mailto:${selectedStudio.studio_email}`} className="text-blue-400 hover:underline">{selectedStudio.studio_email}</a>
                                            )}
                                        </div>
                                        <div className="flex justify-between border-b border-gray-800 pb-2 items-center">
                                            <span className="text-gray-500">Phone</span>
                                            {isEditing ? (
                                                <input type="text" name="studio_phone" value={editForm.studio_phone || ''} onChange={handleChange} className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white w-1/2 text-right" />
                                            ) : (
                                                <span className="text-white">{selectedStudio.studio_phone || 'Not provided'}</span>
                                            )}
                                        </div>
                                        <div className="flex justify-between border-b border-gray-800 pb-2 items-center">
                                            <span className="text-gray-500">Website</span>
                                            {isEditing ? (
                                                <input type="text" name="website" value={editForm.website || ''} onChange={handleChange} className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white w-2/3 text-right" />
                                            ) : selectedStudio.website ? (
                                                <a href={selectedStudio.website.startsWith('http') ? selectedStudio.website : `https://${selectedStudio.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline flex-1 text-right truncate overflow-hidden ml-4">{selectedStudio.website}</a>
                                            ) : (
                                                <span className="text-gray-600">No website</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Location Details */}
                                <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700/50 md:col-span-2">
                                    <h3 className="text-sm uppercase tracking-wider text-blue-400 font-bold mb-4 flex items-center gap-2">
                                        <span className="text-lg">📍</span> Location
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <span className="block text-gray-500 text-xs uppercase mb-1">Address</span>
                                            {isEditing ? (
                                                <textarea name="address" value={editForm.address || ''} onChange={handleChange} className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-white w-full min-h-[80px]" />
                                            ) : (
                                                <p className="text-white bg-gray-800/50 p-3 rounded">{selectedStudio.address || 'Not provided'}</p>
                                            )}
                                        </div>
                                        <div>
                                            <span className="block text-gray-500 text-xs uppercase mb-1">City / Region / Postcode</span>
                                            <div className="flex flex-col gap-2">
                                                <div className="flex justify-between bg-gray-800/50 px-3 py-2 rounded items-center">
                                                    <span className="text-gray-400">City</span>
                                                    {isEditing ? (
                                                        <input type="text" name="city" value={editForm.city || ''} onChange={handleChange} className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white text-right w-1/2" />
                                                    ) : (
                                                        <span className="text-white">{selectedStudio.city || 'Not provided'}</span>
                                                    )}
                                                </div>
                                                <div className="flex justify-between bg-gray-800/50 px-3 py-2 rounded items-center">
                                                    <span className="text-gray-400">Region</span>
                                                    {isEditing ? (
                                                        <input type="text" name="region" value={editForm.region || ''} onChange={handleChange} className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white text-right w-1/2" />
                                                    ) : (
                                                        <span className="text-white">{selectedStudio.region || 'Not provided'}</span>
                                                    )}
                                                </div>
                                                <div className="flex justify-between bg-gray-800/50 px-3 py-2 rounded items-center">
                                                    <span className="text-gray-400">Postcode</span>
                                                    {isEditing ? (
                                                        <input type="text" name="postcode" value={editForm.postcode || ''} onChange={handleChange} className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white text-right w-1/2" />
                                                    ) : (
                                                        <span className="text-white font-mono">{selectedStudio.postcode || 'N/A'}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Raw Data Dump if any non-standard fields exist */}
                                <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700/50 md:col-span-2">
                                    <h3 className="text-sm uppercase tracking-wider text-pink-400 font-bold mb-4 flex items-center gap-2">
                                        <span className="text-lg">⚙️</span> Settings & Formatting
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="flex justify-between bg-gray-800/50 px-3 py-2 rounded">
                                            <span className="text-gray-400">Email Display Name</span>
                                            <span className="text-white">{selectedStudio.email_company_name || selectedStudio.studio_name || 'BookINK'}</span>
                                        </div>
                                        <div className="flex justify-between bg-gray-800/50 px-3 py-2 rounded">
                                            <span className="text-gray-400">Currency</span>
                                            <span className="text-white">{selectedStudio.currency || 'USD'}</span>
                                        </div>
                                        <div className="flex justify-between bg-gray-800/50 px-3 py-2 rounded">
                                            <span className="text-gray-400">Timezone</span>
                                            <span className="text-white">{selectedStudio.timezone || 'UTC'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="sticky bottom-0 bg-gray-900/90 backdrop-blur-md px-6 py-4 border-t border-gray-700 flex justify-end items-center z-10 rounded-b-2xl gap-3">
                            {isEditing ? (
                                <>
                                    <button
                                        onClick={handleCancelEdit}
                                        disabled={isSaving}
                                        className="px-6 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-bold transition-colors border border-gray-700"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold shadow-lg shadow-purple-900/50 transition-colors flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={handleEditClick}
                                        className="px-6 py-2.5 bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white rounded-lg font-bold flex items-center gap-2 border border-blue-600/30 transition-colors"
                                    >
                                        ✏️ Edit Studio
                                    </button>
                                    <button
                                        onClick={() => { setSelectedStudio(null); setIsEditing(false); }}
                                        className="px-6 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-bold transition-colors border border-gray-700 hover:border-gray-500"
                                    >
                                        Close
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudioList;
